import React, {useMemo, useState} from "react";
import {Button, Card, Col, Form, Grid, Input, List, Pagination, Row, Select, Space, Table, Tag, Tooltip, Typography, message} from "antd";
import {DownloadOutlined, ReloadOutlined} from "@ant-design/icons";
import {useAuditActions, useAuditLog} from "./audit-log-query";
import {exportAuditLog} from "./audit-log-api";

const {Text} = Typography;

/**
 * The audit trail (roadmap 0.5).
 *
 * <p>Reading only — there is no edit and no delete, here or on the server. A record that its
 * subjects can change is not a record.
 */
const AuditLog = () => {
    const screens = Grid.useBreakpoint();
    const isDesktop = !!screens.md;
    const [form] = Form.useForm();

    const [pagination, setPagination] = useState({current: 1, pageSize: 20});
    const [filters, setFilters] = useState({});
    const [exporting, setExporting] = useState(false);

    const {data, isLoading, refetch, isFetching} = useAuditLog(
        pagination.current - 1, pagination.pageSize, filters);
    const {data: actionsData} = useAuditActions();

    // The interceptor already unwraps .data, but older call sites in this app defend against both
    // shapes — keep doing so rather than being the one screen that breaks if that ever changes.
    const rows = useMemo(() => data?.content || data?.data?.content || [], [data]);
    const total = useMemo(() => data?.totalElements ?? data?.data?.totalElements ?? rows.length, [data, rows]);
    const actions = useMemo(() => actionsData?.data || actionsData || [], [actionsData]);

    const onFinish = (values) => {
        setFilters({
            action: values.action || undefined,
            outcome: values.outcome || undefined,
            actorName: values.actorName?.trim() || undefined,
            targetId: values.targetId?.trim() || undefined,
        });
        // Applying a filter must always return to the first page, or the operator lands on page 7
        // of a two-page result and concludes there is nothing there.
        setPagination((prev) => ({...prev, current: 1}));
    };

    const onReset = () => {
        form.resetFields();
        setFilters({});
        setPagination({current: 1, pageSize: 20});
    };

    const onExport = async () => {
        setExporting(true);
        try {
            // Exports exactly what is on screen. An export that silently ignored the filters is how
            // the wrong evidence ends up in somebody's report.
            const filename = await exportAuditLog(filters);
            message.success(`فایل «${filename}» دانلود شد`);
        } catch (e) {
            message.error("دانلود خروجی ناموفق بود");
        } finally {
            setExporting(false);
        }
    };

    const outcomeTag = (outcome) => (
        <Tag color={outcome === "FAILURE" ? "error" : "success"}>
            {outcome === "FAILURE" ? "ناموفق" : "موفق"}
        </Tag>
    );

    const actorCell = (row) => (
        <Space direction="vertical" size={0}>
            <Text>{row.actorName || "—"}</Text>
            {row.actorType && <Text type="secondary" style={{fontSize: 12}}>{row.actorType}</Text>}
        </Space>
    );

    const columns = useMemo(() => [
        {
            title: "زمان",
            dataIndex: "occurredAt",
            width: 170,
            render: (v) => (v ? new Date(v).toLocaleString("fa-IR") : "—"),
        },
        {title: "رخداد", dataIndex: "action", width: 200, render: (v) => <Tag>{v}</Tag>},
        {title: "نتیجه", dataIndex: "outcome", width: 90, render: outcomeTag},
        {title: "عامل", key: "actor", width: 170, render: (_, row) => actorCell(row)},
        {
            title: "موضوع",
            key: "target",
            width: 210,
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Text type="secondary" style={{fontSize: 12}}>{row.targetType || "—"}</Text>
                    <Text copyable={!!row.targetId} style={{fontSize: 12}}>{row.targetId || "—"}</Text>
                </Space>
            ),
        },
        {title: "شرح", dataIndex: "summary", render: (v) => v || "—"},
        {
            title: "جزئیات",
            dataIndex: "details",
            width: 120,
            render: (v) => (v ? <Tooltip title={<pre style={{margin: 0}}>{v}</pre>}><Text type="secondary">مشاهده</Text></Tooltip> : "—"),
        },
        {title: "IP", dataIndex: "ipAddress", width: 130, render: (v) => v || "—"},
    ], []);

    return (
        <div style={{padding: 12}}>
            <Card
                title="گزارش رخدادها"
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined/>} onClick={() => refetch()} loading={isFetching}>
                            به‌روزرسانی
                        </Button>
                        <Button type="primary" icon={<DownloadOutlined/>} onClick={onExport} loading={exporting}>
                            خروجی CSV
                        </Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={12}>
                        <Col xs={24} md={6}>
                            <Form.Item label="رخداد" name="action">
                                <Select allowClear showSearch placeholder="همه"
                                        options={(actions || []).map((a) => ({label: a, value: a}))}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={5}>
                            <Form.Item label="نتیجه" name="outcome">
                                <Select allowClear placeholder="همه" options={[
                                    {label: "موفق", value: "SUCCESS"},
                                    {label: "ناموفق", value: "FAILURE"},
                                ]}/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="نام عامل" name="actorName">
                                <Input allowClear placeholder="بخشی از نام"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={7}>
                            <Form.Item label="شناسه موضوع" name="targetId">
                                <Input allowClear placeholder="شناسه گفت‌وگو، پیام یا کاربر"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Space>
                        <Button type="primary" htmlType="submit">فیلتر</Button>
                        <Button onClick={onReset}>پاک کردن</Button>
                    </Space>
                </Form>

                <div style={{height: 16}}/>

                {isDesktop ? (
                    <Table
                        rowKey="id"
                        loading={isLoading}
                        dataSource={rows}
                        columns={columns}
                        pagination={false}
                        size="small"
                        scroll={{x: 1200}}
                        locale={{emptyText: "رخدادی ثبت نشده است"}}
                    />
                ) : (
                    <List
                        loading={isLoading}
                        dataSource={rows}
                        locale={{emptyText: "رخدادی ثبت نشده است"}}
                        renderItem={(row) => (
                            <List.Item>
                                <Space direction="vertical" size={2} style={{width: "100%"}}>
                                    <Space wrap>
                                        <Tag>{row.action}</Tag>
                                        {outcomeTag(row.outcome)}
                                        <Text type="secondary" style={{fontSize: 12}}>
                                            {row.occurredAt ? new Date(row.occurredAt).toLocaleString("fa-IR") : ""}
                                        </Text>
                                    </Space>
                                    <Text>{row.summary || "—"}</Text>
                                    <Text type="secondary" style={{fontSize: 12}}>
                                        {row.actorName || "—"}{row.ipAddress ? ` · ${row.ipAddress}` : ""}
                                    </Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                )}

                {total > 0 && (
                    <div style={{display: "flex", justifyContent: "center", marginTop: 16}}>
                        <Pagination
                            current={pagination.current}
                            pageSize={pagination.pageSize}
                            total={total}
                            showSizeChanger
                            pageSizeOptions={["20", "50", "100"]}
                            onChange={(current, pageSize) => setPagination({current, pageSize})}
                        />
                    </div>
                )}
            </Card>
        </div>
    );
};

export default AuditLog;
